/**
 * Results list for a map search that matched more than one thing.
 *
 * A single hit flies straight to it - a modal you have to dismiss to see one
 * row is friction. This appears when there is a choice to make: several
 * tenements under one holder, a company sample ID that matches across surveys,
 * a name that is both a town and a hole.
 *
 * Rows are grouped by source so the eye can skip whole sections; within a group
 * the order is whatever the service returned, which for tenements is roughly
 * by ID.
 */

import { useEffect, useRef } from 'react';
import { SOURCE_LABELS } from './searchSources';
import { filterStyles } from '../filters/FilterControls';

const s = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 1200,
    background: 'rgba(17,20,22,0.45)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '10vh 16px 16px',
  },
  panel: {
    width: 'min(560px, 100%)', maxHeight: '70vh', display: 'flex', flexDirection: 'column',
    background: 'var(--gf-surface, #FFFFFF)', borderRadius: 14,
    boxShadow: '0 18px 50px rgba(0,0,0,0.28)', overflow: 'hidden',
    fontFamily: 'var(--gf-font-sans, "Poppins", sans-serif)',
    // Focused on open so Escape and the arrow keys work without a click; the
    // ring is the browser's default focus style on a container nobody is
    // actually navigating to, so it is suppressed.
    outline: 'none',
  },
  head: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '14px 16px', borderBottom: '2px solid var(--gf-primary-400, #FD7218)',
  },
  title: { fontSize: 14, fontWeight: 600, color: 'var(--gf-secondary-800, #1F2327)' },
  count: { fontSize: 11.5, color: 'var(--gf-secondary-400, #6B7280)' },
  close: {
    border: 0, background: 'none', cursor: 'pointer', fontSize: 20, lineHeight: 1,
    color: 'var(--gf-secondary-400, #6B7280)', padding: '0 2px',
  },
  list: { overflowY: 'auto', padding: '4px 0 8px' },
  groupLabel: {
    ...filterStyles.label,
    display: 'block', padding: '12px 16px 6px',
  },
  row: {
    display: 'flex', alignItems: 'baseline', gap: 10, width: '100%',
    padding: '9px 16px', border: 0, background: 'none', textAlign: 'left', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  rowTitle: {
    fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
    fontSize: 13, fontWeight: 600, color: 'var(--gf-secondary-800, #1F2327)',
    flex: 'none',
  },
  rowSub: {
    fontSize: 12, color: 'var(--gf-secondary-500, #4B5563)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
  },
  rowMeta: {
    fontSize: 11, color: 'var(--gf-primary-500, #E65A00)', flex: 'none',
    fontVariantNumeric: 'tabular-nums',
  },
  empty: { padding: '28px 16px', textAlign: 'center', color: 'var(--gf-secondary-400, #6B7280)', fontSize: 13 },
};

export function SearchResultsModal({ query, results, onPick, onClose }) {
  const panelRef = useRef(null);

  // Escape closes, and focus starts inside the panel so the keyboard works
  // without a mouse round-trip.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groups = results.reduce((acc, r) => {
    (acc[r.source] = acc[r.source] || []).push(r);
    return acc;
  }, {});

  return (
    <div style={s.backdrop} onClick={onClose} role="presentation">
      <div
        ref={panelRef}
        tabIndex={-1}
        style={s.panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Search results for ${query}`}
      >
        <div style={s.head}>
          <div>
            <div style={s.title}>Results for &ldquo;{query}&rdquo;</div>
            <div style={s.count}>
              {results.length} match{results.length === 1 ? '' : 'es'}
            </div>
          </div>
          <button type="button" style={s.close} onClick={onClose} aria-label="Close results">×</button>
        </div>

        <div style={s.list}>
          {results.length === 0 && (
            <div style={s.empty}>
              Nothing matched. Try a tenement ID (P 51/1234), an A-Number, a company
              sample or hole ID, or a place name.
            </div>
          )}
          {Object.entries(groups).map(([source, rows]) => (
            <div key={source}>
              <span style={s.groupLabel}>{SOURCE_LABELS[source] || source}</span>
              {rows.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  style={s.row}
                  onClick={() => onPick(r)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gf-primary-50, #FFF4ED)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  <span style={s.rowTitle}>{r.title}</span>
                  <span style={s.rowSub}>{r.subtitle}</span>
                  {r.meta ? <span style={s.rowMeta}>{r.meta}</span> : null}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SearchResultsModal;
